import React, { useRef, useCallback, useMemo } from 'react';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import saveAs from 'file-saver';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { useStacklineTheme } from '@stackline/ui';
import { useDorisData } from 'src/utils/Hooks/useDorisData';
import useDorisMetricByWeekId from 'src/serverProxy/useDorisMetricByWeekId';
import AtlasV2DorisSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisSplineChart';

/**
 * Displays visualizations for the content score page
 */
const AtlasV2ContentV2TopSection = () => {
  const formatMetric = useMetricFormatter();
  const theme = useStacklineTheme();

  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const donutChartRef = useRef<HTMLDivElement>(null);

  const { data: dorisData, isLoading: dorisDataLoading } = useDorisData({
    methodName: 'sales/GetSalesMetrics',
    metricType: 'ContentScoreDonut',
    fieldName: 'retailer'
  });

  const weeklyMetricByValues = useDorisMetricByWeekId({
    queryKey: 'content-score-weekly',
    methodName: 'sales/GetSalesMetrics',
    metricType: 'AverageContentScore',
    resultCount: 1200
  });

  const { primaryData, isLoading: isWeeklyLoading } = weeklyMetricByValues;

  const data = [
    ['Title Score', dorisData?.records[0].titleScoreAvg],
    ['Bullets Score', dorisData?.records[0].bulletScoreAvg],
    ['Image Score', dorisData?.records[0].imageScoreAvg],
    ['Video Score', dorisData?.records[0].videoScoreAvg],
    ['Enhanced Score', dorisData?.records[0].aplusScoreAvg]
  ];

  return (
    <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
      <div ref={donutChartRef} style={{ width: '100%', height: '100%' }}>
        <Flex flexDirection="column" gap="xxl">
          <CommonSummaryInfoSubtitle title="Content Utilization" />
          <Flex alignItems="center" gap={88}>
            <div style={{ width: '308px', height: '308px' }}>
              {dorisDataLoading || isWeeklyLoading ? (
                <DonutChartLoading />
              ) : (
                <CircularProgressPercentage percentage={primaryData.at(-1)?.[1] || 0} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              {dorisDataLoading ? (
                <SplineChartLoading width={543} />
              ) : (
                <BeaconChartWithLegend
                  showTitleSummary={false}
                  convertSeriesToDelimitedData={() => {
                    const csvContent = data.map((rowElements) => rowElements.join(',')).join('\n');
                    const blob = new Blob([csvContent], { type: 'text/plain;charset=utf-8' });
                    saveAs(blob, `Content Score.csv`);
                    return Promise.resolve();
                  }}
                  primaryLegendProps={{
                    displayName: legendLabel
                  }}
                  title="Content Score"
                  saveImageRef={donutChartRef}
                >
                  <TopEntityChartV2
                    chartSeries={[
                      {
                        type: 'column',
                        data,
                        color: theme.colors.primary
                      }
                    ]}
                    seriesColors={[theme.colors.primary]}
                    chartOptionsOverride={(defaults) => ({
                      ...defaults,
                      plotOptions: {
                        ...defaults.plotOptions,
                        series: {
                          ...defaults?.series?.plotOptions,
                          dataLabels: {
                            ...defaults.plotOptions?.series?.dataLabels,
                            formatter() {
                              return ` ${formatMetric(this.y, METRICTYPE.PERCENT, {
                                decimalPlaces: 1,
                                showFullValue: false
                              })}`;
                            }
                          }
                        }
                      },
                      chart: {
                        ...defaults.chart,
                        height: 240,
                        spacingTop: 15,
                        marginBottom: 39,
                        spacingBottom: 0
                      }
                    })}
                    leftAlignBars={false}
                  />
                </BeaconChartWithLegend>
              )}
            </div>
          </Flex>
        </Flex>
      </div>
      <AtlasV2DorisSplineChart
        queryKey="content-score-weekly"
        title="Content Utilization"
        showLastWeekValue={true}
        methodName="sales/GetSalesMetrics"
        metricTypeDisplay={METRICTYPE.PERCENT}
        metricType="AverageContentScore"
      />
    </Flex>
  );
};

export default AtlasV2ContentV2TopSection;
